import { get as lodashGet, take as lodashTake, shuffle as lodashShuffle } from 'lodash'
import PresentationApi from '@/api/presentation'
import UserApi from '@/api/user'
import sentry from '@/services/sentry'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

const _state = {
  presentations: [],
  feedbacks: [],
  isLoadingData: false,
}

const SET_HOME_PRESENTATIONS = 'SET_HOME_PRESENTATIONS'
const SET_HOME_FEEDBACKS = 'SET_HOME_FEEDBACKS'
const SET_LOADING_FLAG = 'SET_LOADING_FLAG'
const TEMPLATES_PER_SECTION = 10
const PRESENTATIONS_PER_SECTION = 10
const FEEDBACKS_PER_REQUEST = 20
const FEEDBACKS_PER_SECTION = 4

/**
 * Extracts rating from feedback and filters feedbacks based on rating criteria
 * @param {Array} feedbacks - Array of feedback objects
 * @returns {Array} Filtered feedbacks with rating > 2 or null, with rating property added
 */
export const filterAndEnrichFeedbacks = (feedbacks) => {
  return feedbacks.reduce((res, feedback) => {
    const ratings = feedback.audience?.PrivateFeedbackRatings
    const rating = (Array.isArray(ratings) ? ratings[0] : ratings)?.rating ?? null
    if ((rating && rating > 2) || rating === null) {
      res.push({ ...feedback, rating })
    }
    return res
  }, [])
}

const services = {
  async getPresentations(params) {
    return PresentationApi.getHomePresentations(params)
  },

  async getFeedbacks() {
    const feedbacks = await UserApi.getPrivateFeedbacks({
      limit: FEEDBACKS_PER_REQUEST,
    })

    return filterAndEnrichFeedbacks(feedbacks)
  },
}

const _getters = {
  presentations: (state) => lodashTake(state.presentations, PRESENTATIONS_PER_SECTION),
  feedbacks: (state) => lodashTake(lodashShuffle(state.feedbacks), FEEDBACKS_PER_SECTION),
  fullName: (_, __, rootState) => {
    if (!rootState.currentUser) return ''
    if (!rootState.currentUser.lastName) return rootState.currentUser.firstName
    return `${rootState.currentUser.firstName} ${rootState.currentUser.lastName}`
  },
  notifications: (_, __, rootState) => lodashGet(rootState, 'notificationModule.notifications', []),
  templates: (_, __, rootState) => lodashTake(lodashGet(rootState, 'presentationTemplates.templateAdminPicked', []), TEMPLATES_PER_SECTION),
  isLoadingData: (state) => state.isLoadingData,
  canUseImprovedRecentPresentations: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'improved-recent-presentations')
  },
  canUseImprovedRecentFeedback: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'improved-recent-feedback')
  },
}

const actions = {
  async getHomePageDataAction({ dispatch, commit, getters }) {
    commit(SET_LOADING_FLAG, true)
    try {
      const requestList = [
        dispatch('getHomePresentationsAction'),
        dispatch('getHomeFeedbacksAction'),
        dispatch('presentationTemplates/getTemplatesAdminPickedAction', null, { root: true }),
      ]

      // Only fetch if there are none notifications
      if (!getters.notifications.length) {
        requestList.push(dispatch('notificationModule/getNotificationsAction', null, { root: true }))
      }
      await Promise.all(requestList)
    } finally {
      // Let the loading spinner show for at least 500ms
      setTimeout(() => {
        commit(SET_LOADING_FLAG, false)
      }, 500)
    }
  },
  async getHomePresentationsAction({ commit, getters }) {
    try {
      let presentations = []
      if (getters.canUseImprovedRecentPresentations) {
        // attach query param to fetch the improved list of recent presentations, with only editted presentations that this user own
        presentations = await services.getPresentations({
          onlyMyEditedPresentations: true,
        })
        if (presentations.length === 0) {
          // if no editted presentations, fallback the normal list
          presentations = await services.getPresentations({})
        }
      } else {
        presentations = await services.getPresentations({})
      }

      commit(SET_HOME_PRESENTATIONS, presentations)
    } catch (e) {
      sentry.captureException(e)
    }
  },
  async getHomeFeedbacksAction({ commit }) {
    try {
      const feedbacks = await services.getFeedbacks()
      commit(SET_HOME_FEEDBACKS, feedbacks)
    } catch (e) {
      sentry.captureException(e)
    }
  },
}

const mutations = {
  [SET_HOME_PRESENTATIONS](state, presentations) {
    state.presentations = presentations
  },
  [SET_HOME_FEEDBACKS](state, feedbacks) {
    state.feedbacks = feedbacks
  },
  [SET_LOADING_FLAG](state, isLoadingData) {
    state.isLoadingData = isLoadingData
  },
}

const homePage = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default homePage
